'use client'

import {
  LikertScaleQuestionResponse,
  QuestionType
} from '@/api/services/types/questionTypes'
import { useTestResponse } from '@/hooks/useTestResponse'
import * as React from 'react'
import { useState } from 'react'
import { LikertScaleQuestion } from './LikertScaleQuestion'

interface LikertScaleQuestionTaskProps {
  data?: LikertScaleQuestionResponse
  moduleId: string
  questionId: string
  onComplete: () => void
  onStart: () => void
}

// Przykładowe pytania do testów
const mockQuestions: LikertScaleQuestionResponse[] = [
  {
    id: 'q1',
    questionText: 'Lubię pracować w zespole',
    minLabel: 'Zdecydowanie się nie zgadzam',
    maxLabel: 'Zdecydowanie się zgadzam',
    timeLimitSeconds: 10,
    points: [
      {
        id: 'p1',
        questionId: 'q1',
        skillId: 'skill1',
        degreeValue: 1,
        points: 0,
        imageFileId: null,
        text: null
      },
      {
        id: 'p2',
        questionId: 'q1',
        skillId: 'skill1',
        degreeValue: 2,
        points: 1,
        imageFileId: null,
        text: null
      },
      {
        id: 'p3',
        questionId: 'q1',
        skillId: 'skill1',
        degreeValue: 3,
        points: 2,
        imageFileId: null,
        text: null
      },
      {
        id: 'p4',
        questionId: 'q1',
        skillId: 'skill1',
        degreeValue: 4,
        points: 3,
        imageFileId: null,
        text: null
      },
      {
        id: 'p5',
        questionId: 'q1',
        skillId: 'skill1',
        degreeValue: 5,
        points: 4,
        imageFileId: null,
        text: null
      }
    ]
  },
  {
    id: 'q2',
    questionText: 'Czuję się komfortowo podczas publicznych wystąpień',
    minLabel: 'Zdecydowanie się nie zgadzam',
    maxLabel: 'Zdecydowanie się zgadzam',
    timeLimitSeconds: 8,
    points: [
      {
        id: 'p1',
        questionId: 'q2',
        skillId: 'skill2',
        degreeValue: 1,
        points: 0,
        imageFileId: null,
        text: null
      },
      {
        id: 'p2',
        questionId: 'q2',
        skillId: 'skill2',
        degreeValue: 2,
        points: 1,
        imageFileId: null,
        text: null
      },
      {
        id: 'p3',
        questionId: 'q2',
        skillId: 'skill2',
        degreeValue: 3,
        points: 2,
        imageFileId: null,
        text: null
      },
      {
        id: 'p4',
        questionId: 'q2',
        skillId: 'skill2',
        degreeValue: 4,
        points: 3,
        imageFileId: null,
        text: null
      },
      {
        id: 'p5',
        questionId: 'q2',
        skillId: 'skill2',
        degreeValue: 5,
        points: 4,
        imageFileId: null,
        text: null
      }
    ]
  }
]

export default function LikertScaleQuestionTask({
  data,
  moduleId,
  questionId,
  onComplete,
  onStart
}: LikertScaleQuestionTaskProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedValue, setSelectedValue] = useState<number>()
  const { addQuestionResponse } = useTestResponse()
  const startTimeRef = React.useRef<number>(Date.now())

  React.useEffect(() => {
    startTimeRef.current = Date.now()
    onStart()
  }, [])

  // Używamy danych z API lub mockowych danych
  const questions = data ? [data] : mockQuestions
  const currentQuestion = questions[currentIndex]

  const handleAnswer = async (value?: number) => {
    if (!value) {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(currentIndex + 1)
        setSelectedValue(undefined)
        startTimeRef.current = Date.now()
      } else {
        onComplete()
      }
      return
    }
    // Zapisz odpowiedź w kontekście
    await addQuestionResponse(moduleId, {
      questionId,
      questionType: QuestionType.LIKERT_SCALE_QUESTION,
      likertScaleAnswer: {
        selectedDegree: value,
        responseTimeMillis: Date.now() - startTimeRef.current
      }
    })

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setSelectedValue(undefined)
      startTimeRef.current = Date.now()
    } else {
      onComplete()
    }
  }

  return (
    <div className="flex items-center justify-center">
      {
        <LikertScaleQuestion
          leftLabel={currentQuestion.minLabel}
          onChange={handleAnswer}
          onTimeLimitReached={handleAnswer}
          pointDetails={currentQuestion.points.map((point) => ({
            value: point.degreeValue,
            imageFileId: point.imageFileId,
            text: point.text
          }))}
          points={currentQuestion.points.length}
          question={currentQuestion.questionText}
          rightLabel={currentQuestion.maxLabel}
          selectedValue={selectedValue}
          setSelectedValue={setSelectedValue}
          timeLimitSeconds={currentQuestion.timeLimitSeconds}
        />
      }
    </div>
  )
}
